import type { Ref } from "vue";
import { computed, watch } from "vue";

import type { ActionMenuConfig } from "@/components/menu/ActionMenu/useActionMenu";
import { useActionMenu } from "@/components/menu/ActionMenu/useActionMenu";
import { isLinkDragging } from "@/composables/linkDrag";
import type { Card } from "@/model/card";
import variables from "@/model/variable.module.scss";
import { useBoardStore } from "@/store/board";
import { useDraggingStore } from "@/store/dragging";
import { useLinkStore } from "@/store/link";

interface Props {
  referenceEl: Ref<HTMLElement | null>;
  card: Card;
  disabled: Ref<Readonly<boolean>>;
}

export function useStickyActionMenu({ referenceEl, card, disabled }: Props) {
  // Show the action menu when this sticky is the active card, OR when it is the
  // only selected sticky via the multi-select mechanism (rect / cmd+click).
  const isSoleSelectedSticky = computed(() => {
    if (useBoardStore().activeCardId === card.id) return true;

    const selected = useBoardStore().selectedStickyIds;
    return selected.length === 1 && selected[0] === card.id;
  });

  const open = computed(
    () =>
      isSoleSelectedSticky.value && !useDraggingStore().dragging[card.id] && !isLinkDragging.value,
  );

  const size = parseInt(variables.stickySize.replace("px", ""));
  const config = computed<ActionMenuConfig>(() => {
    const isPinned = useLinkStore().isPinned(card.id);
    return {
      placement: "top",
      offset: ({ reference }) => calculateOffset(reference.height, isPinned, size),
    };
  });

  const { isOpen, updateMenu, updateRef } = useActionMenu({ referenceEl, open, disabled, config });

  // update the action menu position when the card type, or priority changes
  watch([() => card.type, () => card.priority], updateMenu);

  return { isOpen, updateRef };
}

function calculateOffset(
  referenceHeight: number,
  isPinned: boolean,
  size: number,
): [number, number] {
  const multiplier = referenceHeight > 0 ? size / referenceHeight : 1;
  const offsetY = isPinned ? 11 : 5;

  return [0, offsetY / multiplier];
}
